import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Badge from '@material-ui/core/Badge';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import CachedIcon from '@material-ui/icons/Cached';
import ClearIcon from '@material-ui/icons/Clear';
import FilterListIcon from '@material-ui/icons/FilterList';
import PrintIcon from '@material-ui/icons/Print';
import SortIcon from '@material-ui/icons/Sort';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import {
  TableContext,
  TableSelectedContext,
  TableToolbarButton,
  TableToolbarProps,
} from '@eas/common-web';
import { NamedSettingsButton } from '@composite/custom-named-settings/table-named-settings/table-named-settings-button';
import {
  ParsedTableNamedSettingsContext,
  ParsedTableNamedSettingsProvider,
} from '@composite/custom-named-settings/table-named-settings/table-named-settings-context';
import { consumeDepartmentTableFilterSync } from '../department-table-filter-sync';
import { PlannerContext } from '../planner-context';
import { consumeShowSelectedWorkOrdersTableFilterSync } from '../show-selected-workorders-table-filter-sync';
import { WorkOrderTableCustomSearchBar } from './work-order-custom-searchbar';
import { ClearWorkOrderTableCacheContext } from './work-order-table-websocket-utils';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  selection: {
    display: 'flex',
    alignItems: 'center',
    color: 'white',
    background: theme.palette.primary.main,
    fontSize: 'larger',
    width: '100%',
    paddingLeft: 10,
  },
  toolbarWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    flex: '1 1 auto',
    overflow: 'hidden',
  },
  savedItems: {
    display: 'flex',
    gap: 4,
    marginLeft: 8,
    overflowX: 'auto',
    flex: '1 1 auto',
  },
  filtersWarning: {
    backgroundColor: `${theme.palette.warning.light} !important`,
  },
}));

export const WorkOrderTableCustomToolbar = (props: TableToolbarProps) => {
  const { tableId } = useContext(TableContext);

  return (
    <ParsedTableNamedSettingsProvider tableId={tableId}>
      <WorkOrderTableCustomToolbarInner {...props} />
    </ParsedTableNamedSettingsProvider>
  );
};

export const WorkOrderTableCustomToolbarInner = (props: TableToolbarProps) => {
  const tableCtx = useContext(TableContext);
  const { selected } = useContext(TableSelectedContext);
  const { savedItems, selectedItem, selectSaved } = useContext(
    ParsedTableNamedSettingsContext
  );
  const { clearCache, registerListener } = useContext(
    ClearWorkOrderTableCacheContext
  );
  const {
    departmentTableFilterSyncSignal,
    showSelectedWorkOrdersTableFilterSyncSignal,
    lastAppliedShowSelectedSignalKey,
    setLastAppliedShowSelectedSignalKey,
    setCalendarWorkOrderTableFilterIds,
    clearCalendarWorkOrderTableFilter,
  } = useContext(PlannerContext);

  const intl = useIntl();

  const toolbarClasses = useStyles();

  const {
    source,
    tableId,
    showResetSortsButton,
    showColumnButton,
    showFilterButton,
    filtersState,
    resetSorts,
    disabledResetSortsButton,
    disabledColumnButton,
    disabledFilterButton,
    openColumnDialog,
    openFilterDialog,
    showReportButton,
    disabledReportButton,
    openExportDialog,
    classes,
    setFiltersState,
    refresh,
  } = tableCtx;

  const filterCount = filtersState.filter((filter) => filter.enabled).length;

  const [changesCount, setChangesCount] = useState(0);
  const lastAppliedDepartmentSignalKeyRef = useRef<number>();

  useEffect(() => {
    if (registerListener) {
      return registerListener((count: number) => {
        setChangesCount(count);
      });
    }
  }, [registerListener]);

  useEffect(() => {
    if (!departmentTableFilterSyncSignal || !setFiltersState) {
      return;
    }

    let nextAppliedSignalKey = lastAppliedDepartmentSignalKeyRef.current;
    let shouldClearSaved = false;

    setFiltersState((prev) => {
      const result = consumeDepartmentTableFilterSync({
        filters: tableCtx.filters,
        prevFiltersState: prev,
        signal: departmentTableFilterSyncSignal,
        lastAppliedSignalKey: lastAppliedDepartmentSignalKeyRef.current,
      });

      nextAppliedSignalKey = result.nextAppliedSignalKey;
      shouldClearSaved = result.shouldClearSaved;

      return result.shouldApply ? result.nextFiltersState : prev;
    });

    if (!shouldClearSaved) {
      return;
    }

    selectSaved(undefined);
    lastAppliedDepartmentSignalKeyRef.current = nextAppliedSignalKey;
  }, [
    departmentTableFilterSyncSignal,
    selectSaved,
    setFiltersState,
    tableCtx.filters,
  ]);

  useEffect(() => {
    if (!showSelectedWorkOrdersTableFilterSyncSignal || !setFiltersState) {
      return;
    }

    let nextAppliedSignalKey = lastAppliedShowSelectedSignalKey;
    let shouldClearSaved = false;
    let calendarFilterIds: string[] | undefined;

    setFiltersState((prev) => {
      const result = consumeShowSelectedWorkOrdersTableFilterSync({
        filters: tableCtx.filters,
        prevFiltersState: prev,
        signal: showSelectedWorkOrdersTableFilterSyncSignal,
        lastAppliedSignalKey: lastAppliedShowSelectedSignalKey,
      });

      nextAppliedSignalKey = result.nextAppliedSignalKey;
      shouldClearSaved = result.shouldClearSaved;
      calendarFilterIds = result.calendarFilterIds;

      return result.shouldApply ? result.nextFiltersState : prev;
    });

    if (!shouldClearSaved) {
      return;
    }

    selectSaved(undefined);
    setCalendarWorkOrderTableFilterIds(calendarFilterIds);
    setLastAppliedShowSelectedSignalKey(nextAppliedSignalKey);
  }, [
    lastAppliedShowSelectedSignalKey,
    selectSaved,
    setFiltersState,
    setCalendarWorkOrderTableFilterIds,
    setLastAppliedShowSelectedSignalKey,
    showSelectedWorkOrdersTableFilterSyncSignal,
    tableCtx.filters,
  ]);

  const showFiltersWarning = filterCount > 0 && changesCount > 0;

  return (
    <>
      <WorkOrderTableCustomSearchBar />
      <div className={toolbarClasses.wrapper}>
        {selected.length > 0 ? (
          <div className={toolbarClasses.selection}>
            Označené pracovné príkazy: {selected.length}
          </div>
        ) : (
          <div className={toolbarClasses.toolbarWrapper}>
            <span style={{ flexShrink: 0 }}>
              Pracovné príkazy ({source.items.length} / {source.count})
            </span>
            <div className={toolbarClasses.savedItems}>
              {savedItems
                .filter((item) => item.settings.isTop)
                .map((item) => {
                  return (
                    <Button
                      style={{ padding: '0 8px', flexShrink: 0 }}
                      size="small"
                      variant={
                        item.id === selectedItem ? 'contained' : 'outlined'
                      }
                      color={item.id === selectedItem ? 'primary' : 'default'}
                      key={item.id}
                      onClick={() => {
                        clearCalendarWorkOrderTableFilter();
                        selectSaved(item);
                      }}
                    >
                      {item.name}
                    </Button>
                  );
                })}
            </div>
          </div>
        )}

        <ul className={classes.toolbarButtonList}>
          {props?.before}
          <TableToolbarButton
            // reset filters
            label={<ClearIcon />}
            onClick={() => {
              setFiltersState?.((filtersState) => {
                return filtersState.map((state) => ({
                  ...state,
                  value: null,
                  enabled: false,
                  object: null,
                  filters: [],
                  selectFilter: [],
                  numberFilter: null,
                }));
              });
              selectSaved(undefined);
              clearCalendarWorkOrderTableFilter();
            }}
            tooltip={intl.formatMessage({
              id: 'EAS_TABLE_TOOLBAR_BTN_RESET',
              defaultMessage: 'Resetovať všetky filtre',
            })}
          />
          <TableToolbarButton
            label={<CachedIcon />}
            onClick={() => {
              clearCache?.();
              refresh();
            }}
            tooltip={intl.formatMessage({
              id: 'EAS_TABLE_TOOLBAR_BTN_REFRESH',
              defaultMessage: 'Obnoviť dáta',
            })}
            dataCy="EAS__TABLE__TOOLBAR__REFRESH"
          />
          {showResetSortsButton && (
            <TableToolbarButton
              label={<SortIcon />}
              disabled={disabledResetSortsButton}
              onClick={resetSorts}
              tooltip={intl.formatMessage({
                id: 'EAS_TABLE_TOOLBAR_BTN_RESET_SORTS',
                defaultMessage: 'Obnovení výchozího řazení',
              })}
              dataCy="EAS__TABLE__TOOLBAR__RESET_SORTS"
            />
          )}
          {showColumnButton && (
            <TableToolbarButton
              label={<ViewColumnIcon />}
              disabled={disabledColumnButton}
              onClick={openColumnDialog}
              tooltip={intl.formatMessage({
                id: 'EAS_TABLE_TOOLBAR_BTN_COLUMN_DIALOG_OPEN',
                defaultMessage: 'Nastavení sloupců',
              })}
            />
          )}
          {showFilterButton && (
            <Badge
              badgeContent={showFiltersWarning ? '!!' : filterCount}
              color="error"
            >
              <TableToolbarButton
                label={<FilterListIcon />}
                className={
                  showFiltersWarning ? toolbarClasses.filtersWarning : undefined
                }
                disabled={disabledFilterButton}
                onClick={openFilterDialog}
                tooltip={
                  showFiltersWarning
                    ? 'Obsah tabulky bol zmenený. Filtre už nemusia byť aktuálne. Toto upozornenie odstránite obnovením tabuľky.'
                    : 'Nastavenie filtrov'
                }
              />
            </Badge>
          )}

          <NamedSettingsButton
            tag={tableId}
            disabled={false}
            onBeforeSelectSaved={clearCalendarWorkOrderTableFilter}
          />

          {showReportButton && (
            <TableToolbarButton
              label={<PrintIcon />}
              disabled={disabledReportButton}
              onClick={openExportDialog}
              tooltip={intl.formatMessage({
                id: 'EAS_TABLE_TOOLBAR_BTN_EXPORT_DIALOG_OPEN',
                defaultMessage: 'Export',
              })}
            />
          )}

          {props?.after}
        </ul>
      </div>
    </>
  );
};
